import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/browser';

import { useDynamicMethods, useVoip } from '$app-web/components/chat/state/chat-client.state';

import { CALL_PHASES, CALL_TYPES, CallStateChangedPayload, CallType } from './types';

type ReportCallState = (payload: CallStateChangedPayload) => void;

interface VoipState {
    activeCallData?: {
        id?: string;
        roomId?: string;
        type?: string;
        status?: string;
    };
}

interface DynamicMethods {
    getRoomTitle?: (roomId: string) => string | undefined;
}

function safeGetRoomTitle(dynamicMethods: DynamicMethods, roomId: string | undefined): string | null {
    if (!roomId || typeof dynamicMethods.getRoomTitle !== 'function') return null;
    return dynamicMethods.getRoomTitle(roomId) ?? null;
}

const ACTIVE_STATUSES = new Set(['ACTIVE', 'RINGING', 'WAITING_FOR_PERMISSION']);

function deriveCallType(value: string | undefined): CallType {
    return value === CALL_TYPES.VIDEO ? CALL_TYPES.VIDEO : CALL_TYPES.VOICE;
}

export function useCallStateReporter(report: ReportCallState) {
    const voip = useVoip() as VoipState;
    const dynamicMethods = useDynamicMethods() as DynamicMethods;

    const activeCall = voip?.activeCallData ?? null;
    const activeStatus = activeCall?.status ?? null;
    const isActive = activeStatus !== null && ACTIVE_STATUSES.has(activeStatus);
    const activeCallId = isActive ? activeCall?.id ?? null : null;
    const activeRoomId = activeCall?.roomId;
    const activeCallType = activeCall?.type;

    const lastReportedCallIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (activeCallId === null) {
            if (lastReportedCallIdRef.current !== null) {
                const endedCallId = lastReportedCallIdRef.current;
                try {
                    report({ state: CALL_PHASES.ENDED, callId: endedCallId, callerName: null, callType: null });
                    lastReportedCallIdRef.current = null;
                } catch (error) {
                    Sentry.withScope((scope) => {
                        scope.setTag('catch_in', 'useCallStateReporter');
                        scope.setExtras({ phase: 'ended', callId: endedCallId });
                        Sentry.captureException(error);
                    });
                }
            }
            return;
        }

        if (lastReportedCallIdRef.current === activeCallId) {
            return;
        }

        const resolvedCallerName = safeGetRoomTitle(dynamicMethods, activeRoomId);
        try {
            report({
                state: CALL_PHASES.ACTIVE,
                callId: activeCallId,
                callerName: resolvedCallerName,
                callType: deriveCallType(activeCallType),
            });
            lastReportedCallIdRef.current = activeCallId;
        } catch (error) {
            Sentry.withScope((scope) => {
                scope.setTag('catch_in', 'useCallStateReporter');
                scope.setExtras({ phase: 'active', callId: activeCallId, roomId: activeRoomId, callType: activeCallType });
                Sentry.captureException(error);
            });
        }
    }, [activeCallId, activeRoomId, activeCallType, dynamicMethods, report]);

    const reportRef = useRef(report);
    useEffect(() => {
        reportRef.current = report;
    });
    useEffect(() => {
        return () => {
            const endedCallId = lastReportedCallIdRef.current;
            if (endedCallId === null) return;
            try {
                reportRef.current({ state: CALL_PHASES.ENDED, callId: endedCallId, callerName: null, callType: null });
            } catch (error) {
                Sentry.withScope((scope) => {
                    scope.setTag('catch_in', 'useCallStateReporter');
                    scope.setExtras({ phase: 'ended-unmount', callId: endedCallId });
                    Sentry.captureException(error);
                });
            }
        };
    }, []);
}
